import { memo, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { UserPlus } from "lucide-react";
import type { GraduationResponseDTO } from "../types/graduation.types";
import { SellerMultiSelectField } from "./SellerMultiSelectField";
import { TagMultiSelectField } from "./TagMultiSelectField";
import { ClientSelectField } from "./ClientSelectField";
import { ClientQuickCreateDialog } from "@/features/clients/components/ClientQuickCreateDialog";
import type { ClientResponseDTO } from "@/features/clients/types/client.types";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { applyFieldErrorsToForm } from "@/shared/lib/form-error";

const SELECT_CLASSES =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

const graduationSchema = z
  .object({
    eventName: z
      .string()
      .min(1, "Nome do evento é obrigatório")
      .max(255, "Máximo 255 caracteres"),
    eventDate: z.string().min(1, "Data do evento é obrigatória"),
    totalValue: z
      .number({ message: "Valor total é obrigatório" })
      .positive("Valor deve ser maior que zero"),
    paymentMethod: z.enum(["PIX", "CREDIT_CARD", "DEBIT_CARD", "CASH"]),
    description: z.string().optional(),
    invoiceObservation: z
      .string()
      .max(2000, "Máximo 2000 caracteres")
      .optional(),
    sellerIds: z.array(z.number()).min(1, "Selecione ao menos um vendedor"),
    tagIds: z.array(z.number()),
    skipInvoiceEmission: z.boolean(),
    clientId: z.number().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.skipInvoiceEmission && !data.clientId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Cliente obrigatório quando a nota fiscal será emitida",
        path: ["clientId"],
      });
    }
  });

export type GraduationFormData = z.infer<typeof graduationSchema>;

type DialogMode = "create" | "edit";

interface GraduationFormDialogProps {
  open: boolean;
  mode: DialogMode;
  loading: boolean;
  graduation: GraduationResponseDTO | null;
  onClose: () => void;
  onSubmit: (data: GraduationFormData) => Promise<void>;
}

function isoToDatetimeLocal(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const EMPTY_DEFAULTS: GraduationFormData = {
  eventName: "",
  eventDate: "",
  totalValue: 0,
  paymentMethod: "PIX",
  description: "",
  invoiceObservation: "",
  sellerIds: [],
  tagIds: [],
  skipInvoiceEmission: false,
  clientId: null,
};

/**
 * Dialog único de criação e edição de uma formatura.
 *
 * O form é resetado a partir de `graduation` quando o dialog abre em
 * modo `edit` ou quando o alvo muda. Em modo `create` reseta para
 * defaults vazios.
 *
 * @example
 * <GraduationFormDialog
 *   open={dialogMode === 'create' || dialogMode === 'edit'}
 *   mode={dialogMode === 'create' ? 'create' : 'edit'}
 *   loading={dialogLoading}
 *   graduation={selectedGraduation}
 *   onClose={closeDialog}
 *   onSubmit={handleFormSubmit}
 * />
 */
export const GraduationFormDialog = memo(function GraduationFormDialog({
  open,
  mode,
  loading,
  graduation,
  onClose,
  onSubmit,
}: GraduationFormDialogProps) {
  const form = useForm<GraduationFormData>({
    resolver: zodResolver(graduationSchema),
    defaultValues: EMPTY_DEFAULTS,
  });

  const skipInvoiceEmission = useWatch({
    control: form.control,
    name: "skipInvoiceEmission",
  });

  const [createClientOpen, setCreateClientOpen] = useState(false);

  const handleClientCreated = (client: ClientResponseDTO) => {
    form.setValue("clientId", client.id, { shouldValidate: true });
  };

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && graduation) {
      form.reset({
        eventName: graduation.eventName,
        eventDate: isoToDatetimeLocal(graduation.eventDate),
        totalValue: graduation.totalValue,
        paymentMethod: graduation.paymentMethod,
        description: graduation.description ?? "",
        invoiceObservation: "",
        sellerIds: graduation.sellerIds,
        tagIds: graduation.tagIds,
        skipInvoiceEmission: graduation.skipInvoiceEmission ?? false,
        clientId: graduation.clientId ?? null,
      });
    } else {
      form.reset(EMPTY_DEFAULTS);
    }
  }, [open, mode, graduation, form]);

  const handleValid = async (data: GraduationFormData) => {
    try {
      await onSubmit(data);
    } catch (error: unknown) {
      if (applyFieldErrorsToForm(error, form)) return;
      toast.error(
        getApiErrorMessage(
          error,
          mode === "create"
            ? "Erro ao criar formatura"
            : "Erro ao atualizar formatura",
        ),
      );
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {mode === "create" ? "Nova Formatura" : "Editar Formatura"}
            </DialogTitle>
            <DialogDescription>
              {mode === "create"
                ? "Preencha os dados do evento de formatura."
                : `Atualize os dados da formatura #${graduation?.idGraduation}.`}
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleValid)}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="eventName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome do Evento *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Formatura Medicina UFBA 2024"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="eventDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Data do Evento *</FormLabel>
                      <FormControl>
                        <Input type="datetime-local" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="totalValue"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Valor Total *</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0,00"
                          value={field.value === 0 ? "" : field.value}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === "") {
                              field.onChange(0);
                              return;
                            }
                            const parsed = Number(raw);
                            if (Number.isNaN(parsed)) return;
                            field.onChange(parsed);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="paymentMethod"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Método de Pagamento *</FormLabel>
                    <FormControl>
                      <select {...field} className={SELECT_CLASSES}>
                        <option value="PIX">PIX</option>
                        <option value="CREDIT_CARD">Crédito</option>
                        <option value="DEBIT_CARD">Débito</option>
                        <option value="CASH">Dinheiro</option>
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="skipInvoiceEmission"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <label className="flex items-start gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={field.value}
                          onChange={(e) => {
                            field.onChange(e.target.checked);
                            // Ao pular a emissão, descarta a observação digitada para
                            // não persisti-la numa nota que nunca será emitida.
                            if (e.target.checked) {
                              form.setValue("invoiceObservation", "");
                            }
                          }}
                          disabled={loading}
                        />
                        <span>
                          Não emitir nota fiscal automaticamente
                          <span className="block text-muted-foreground">
                            Quando ativado, a nota fiscal desta formatura não
                            será emitida automaticamente.
                          </span>
                        </span>
                      </label>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="clientId"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel htmlFor="graduation-form-client">
                        Cliente (tomador NFS-e)
                        {!skipInvoiceEmission && (
                          <span className="text-destructive ml-0.5">*</span>
                        )}
                      </FormLabel>
                      <button
                        type="button"
                        onClick={() => setCreateClientOpen(true)}
                        disabled={loading}
                        className="flex items-center gap-1 text-xs text-primary hover:underline disabled:opacity-50"
                      >
                        <UserPlus className="w-3 h-3" />
                        Novo cliente
                      </button>
                    </div>
                    <FormControl>
                      <ClientSelectField
                        inputId="graduation-form-client"
                        value={field.value ?? null}
                        onChange={field.onChange}
                        disabled={loading}
                      />
                    </FormControl>
                    {skipInvoiceEmission && (
                      <p className="text-xs text-muted-foreground">
                        Opcional — nota fiscal não será emitida.
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {!skipInvoiceEmission && (
                <FormField
                  control={form.control}
                  name="invoiceObservation"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Observação da NF</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Descrição que aparece na nota fiscal"
                          rows={3}
                          maxLength={2000}
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        Opcional. Em branco usa o padrão da loja.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descrição</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Observações sobre o evento (opcional)"
                        rows={3}
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="sellerIds"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="graduation-form-sellers">
                      Vendedores *
                    </FormLabel>
                    <FormControl>
                      <SellerMultiSelectField
                        inputId="graduation-form-sellers"
                        value={field.value}
                        onChange={field.onChange}
                        disabled={loading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tagIds"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="graduation-form-tags">Tags</FormLabel>
                    <FormControl>
                      <TagMultiSelectField
                        inputId="graduation-form-tags"
                        value={field.value}
                        onChange={field.onChange}
                        disabled={loading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading
                    ? "Salvando..."
                    : mode === "create"
                      ? "Criar Formatura"
                      : "Salvar Alterações"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      <ClientQuickCreateDialog
        open={createClientOpen}
        onClose={() => setCreateClientOpen(false)}
        onCreated={handleClientCreated}
      />
    </>
  );
});
